import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Calendar as CalendarIcon, 
  MessageSquare, 
  DollarSign, 
  FileText, 
  Users, 
  ClipboardList, 
  Stethoscope,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  Bell,
  CreditCard,
  Receipt,
  Phone,
  Shield,
  Mail,
  MessageCircle,
  BarChart3,
  HelpCircle,
  Video,
  Zap
, BookOpen, Camera, Sparkles, Briefcase} from 'lucide-react';
import { authAPI, messagesAPI } from '@/utils/api';
import { toast } from 'sonner';

const Layout = ({ children, setIsAuthenticated }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await messagesAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
    navigate('/auth');
  };

  // Professional users menu items
  const professionalItems = ['attorney', 'court_rep', 'provider'].includes(user?.role) ? [
    { icon: FileText, label: 'Case Files', path: '/case-files' },
    { icon: Users, label: 'Invite Parent', path: '/invite-parent' },
  ] : [];

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    ...professionalItems,
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: HelpCircle, label: 'Help & FAQ', path: '/help' },
    { icon: Phone, label: 'Calls', path: '/calls' },
    { icon: Briefcase, label: 'My Case', path: '/my-case' },
    { icon: MessageSquare, label: 'Messages', path: '/messages', badge: unreadCount },
    { icon: MessageCircle, label: 'Text Messages', path: '/text-messages' },
    { icon: Receipt, label: 'Invoices', path: '/invoices' },
    { icon: DollarSign, label: 'Expenses', path: '/expenses' },
    { icon: FileText, label: 'Documents', path: '/documents' },
    { icon: FileText, label: 'Reports', path: '/reports' },
    { icon: Users, label: 'Children', path: '/children' },
    { icon: BookOpen, label: 'Info Bank', path: '/info-bank' },
    { icon: Camera, label: 'Moments', path: '/moments' },
    { icon: CalendarIcon, label: 'Calendar', path: '/calendar' },
    { icon: CalendarIcon, label: 'Schedule Changes', path: '/schedule-changes' },
    { icon: ClipboardList, label: 'Parenting Plans', path: '/parenting-plans' },
    { icon: Stethoscope, label: 'Professionals', path: '/professionals' },
    { icon: Sparkles, label: 'Plans', path: '/plans' },
    { icon: CreditCard, label: 'Billing', path: '/billing' },
    { icon: Shield, label: 'Waiver & Terms', path: '/waiver' },
    { icon: Mail, label: 'Contact Us', path: '/contact' },
    { icon: Bell, label: 'Notifications', path: '/notifications' },
    { icon: Shield, label: 'Activity Log', path: '/activity-log' },
    ...(user?.role === 'admin' ? [
      { icon: Video, label: 'Twilio Setup', path: '/twilio-integration' },
      { icon: Zap, label: 'Stripe Setup', path: '/stripe-integration' },
    ] : []),
    { icon: SettingsIcon, label: 'Settings', path: '/settings' },
  ];

  return (
    <div className="flex h-screen bg-slate-900">
      {/* Skip navigation link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg"
        aria-label="Skip to main content"
      >
        Skip to main content
      </a>
      
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex flex-col">
              <div className="flex items-center space-x-2 mb-1">
                <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent" style={{fontFamily: 'Plus Jakarta Sans'}}>FamilyTrails</h1>
              </div>
              <p className="text-xs text-slate-400 ml-10" style={{fontFamily: 'DM Sans', fontWeight: 300}}>
                
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User info */}
          {user && (
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white flex items-center justify-center font-semibold shadow-lg">
                  {user.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-slate-100">{user.full_name}</p>
                  <p className="text-xs text-slate-400">{user.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav 
            className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800 hover:scrollbar-thumb-slate-500"
            aria-label="Main navigation"
            role="navigation"
          >
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  aria-label={`Navigate to ${item.label}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center justify-between px-4 py-3 mb-2 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                    isActive
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <span 
                      className="px-2 py-1 text-xs font-bold text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full shadow-md cursor-pointer"
                      aria-label={`${item.badge} unread notifications`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.path);
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-700">
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              aria-label="Logout from your account"
              className="flex items-center space-x-3 w-full px-4 py-3 text-rose-400 hover:bg-slate-700 hover:text-rose-300 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 focus:ring-offset-slate-800"
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-900">
        {/* Top bar */}
        <header className="bg-slate-800 border-b border-slate-700 lg:hidden">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-slate-400 hover:text-slate-200"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex flex-col items-center">
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent" style={{fontFamily: 'Plus Jakarta Sans'}}>FamilyTrails</h1>
              <p className="text-[10px] text-slate-500" style={{fontFamily: 'DM Sans'}}></p>
            </div>
            <Bell className="w-6 h-6 text-slate-400" />
          </div>
        </header>

        {/* Page content */}
        <main 
          id="main-content"
          className="flex-1 overflow-y-auto p-6 bg-slate-900"
          role="main"
          aria-label="Main content area"
        >
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
